// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a file's contents shown by what it is (previewKindFor): pictures with zoom, PDFs, Word,
// Excel and PowerPoint, highlighted text and code, CSV and TSV tables, HTML pages, Markdown, audio
// and video, or why it can't show (too large, a kind Konvo can't show). The one body every preview
// uses, inside FilePreviewWindow; each caller brings its own loader.

import {PanZoomSurface} from '@app/features/messaging/components/modals/media_modal/pan_zoom/PanZoomSurface';
import {Spinner} from '@app/features/ui/components/Spinner';
import {FileIcon} from '@app/features/ui/icons/Icons';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {lazy, type ReactNode, Suspense, useEffect, useMemo, useState} from 'react';
import styles from './FileContentPreview.module.css';
import {
	csvDelimiterFor,
	isTooLargeToPreview,
	type PreviewAttachment,
	type PreviewKind,
	previewKindFor,
	shouldRenderPdfInApp,
} from './FilePreviewKind';

const OfficeAttachmentPreview = lazy(() => import('@app/features/messaging/components/OfficeAttachmentPreview'));
const DocumentAttachmentView = lazy(() => import('@app/features/document_preview/DocumentAttachmentView'));
const PdfAttachmentPreview = lazy(() => import('@app/features/messaging/components/PdfAttachmentPreview'));
const CsvFilePreview = lazy(() => import('@app/features/file_preview/CsvFilePreview'));
const TextFilePreview = lazy(() => import('@app/features/file_preview/TextFilePreview'));

const NO_PREVIEW_DESCRIPTOR = msg({
	message: 'No preview for this file type. Download it to open it.',
	comment: 'Attachment preview fallback message.',
});
const TOO_LARGE_DESCRIPTOR = msg({
	message: 'This file is too large to preview. Download it to open it.',
	comment: 'File preview: a text, CSV, Markdown or HTML file over the preview size limit.',
});
const LOAD_FAILED_DESCRIPTOR = msg({
	message: 'Could not load this attachment.',
	comment: 'Attachment preview error message.',
});

/** A file being loaded for its preview, and what it turned out to be. */
export interface FileContent {
	/** Its name and the type and size it came with, once loaded. */
	loaded: PreviewAttachment;
	kind: PreviewKind;
	blobUrl: string | null;
	/** Why loading failed (whatever the loader threw), or null. */
	error: unknown;
	failed: boolean;
	loading: boolean;
	retry: () => void;
	/** For a picture or video that loaded but can't be drawn. */
	fail: () => void;
	officeBlob: Blob | null;
	pdfBlob: Blob | null;
	text: string | null;
	/** Saves the loaded file under its name; nothing until it has loaded. */
	download: () => void;
}

/** Saves a blob: URL under a file name. */
export function downloadBlobUrl(url: string, name: string): void {
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = name;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
}

export function useFileContent(attachment: PreviewAttachment, loadBlob: () => Promise<Blob | null>): FileContent {
	const [loaded, setLoaded] = useState<PreviewAttachment>(attachment);
	const kind = useMemo(() => previewKindFor(loaded), [loaded]);
	const [blobUrl, setBlobUrl] = useState<string | null>(null);
	const [officeBlob, setOfficeBlob] = useState<Blob | null>(null);
	const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
	const [text, setText] = useState<string | null>(null);
	const [error, setError] = useState<{reason: unknown} | null>(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let cancelled = false;
		let url: string | null = null;
		setBlobUrl(null);
		setOfficeBlob(null);
		setPdfBlob(null);
		setText(null);
		setError(null);
		void loadBlob()
			.then(async (blob) => {
				if (cancelled) return;
				if (!blob) throw new Error('Attachment unavailable');
				const metadata = {...attachment, content_type: blob.type || attachment.content_type, size: blob.size};
				const loadedKind = previewKindFor(metadata);
				setLoaded(metadata);
				const previewBlob = loadedKind === 'pdf' ? blob.slice(0, blob.size, 'application/pdf') : blob;
				url = URL.createObjectURL(previewBlob);
				if (loadedKind === 'pdf') setPdfBlob(previewBlob);
				setBlobUrl(url);
				if (loadedKind === 'docx' || loadedKind === 'spreadsheet' || loadedKind === 'presentation') setOfficeBlob(blob);
				if (loadedKind === 'text' || loadedKind === 'csv' || loadedKind === 'html' || loadedKind === 'markdown') {
					const content = await blob.text();
					if (!cancelled) setText(content);
				}
			})
			.catch((reason: unknown) => {
				if (!cancelled) setError({reason});
			});
		return () => {
			cancelled = true;
			if (url) URL.revokeObjectURL(url);
		};
	}, [loadBlob, attachment, attempt]);

	return {
		loaded,
		kind,
		blobUrl,
		error: error?.reason ?? null,
		failed: error !== null,
		loading: !blobUrl && !error,
		retry: () => setAttempt((n) => n + 1),
		fail: () => setError({reason: new Error('Attachment could not be shown')}),
		officeBlob,
		pdfBlob,
		text,
		download: () => {
			if (blobUrl) downloadBlobUrl(blobUrl, attachment.name);
		},
	};
}

function Loading() {
	return (
		<div className={styles.empty}>
			<Spinner />
		</div>
	);
}

/** The file itself, drawn by its kind. `failedView` replaces the plain "Could not load" line. */
export function FileContentBody({
	content,
	name,
	failedView,
}: {
	content: FileContent;
	name: string;
	failedView?: ReactNode;
}) {
	const {i18n} = useLingui();
	const pdfInApp = useMemo(shouldRenderPdfInApp, []);
	const {kind, blobUrl, officeBlob, pdfBlob, text} = content;
	if (content.failed) return failedView ?? <div className={styles.empty}>{i18n._(LOAD_FAILED_DESCRIPTOR)}</div>;
	if (content.loading) return <Loading />;
	switch (kind) {
		case 'docx':
		case 'spreadsheet':
		case 'presentation':
			// OfficeAttachmentPreview owns its own spinner, error and empty states; never render nothing here.
			if (!officeBlob) return <Loading />;
			return (
				<Suspense fallback={<Loading />}>
					<OfficeAttachmentPreview blob={officeBlob} kind={kind} name={name} />
				</Suspense>
			);
		case 'image':
			// Same zoom as the media viewer: click or double tap, scroll wheel, pinch, drag to pan.
			return (
				<PanZoomSurface
					zoomedScale="auto"
					maxScale={8}
					clickToToggleZoom
					doubleTapToToggleZoom
					resetKey={blobUrl}
					data-flx="konvo-mail.attachment-preview.image-zoom"
				>
					<img className={styles.image} src={blobUrl!} alt={name} draggable={false} onError={content.fail} />
				</PanZoomSurface>
			);
		case 'pdf':
			if (!pdfInApp) return <iframe className={styles.frame} src={`${blobUrl}#toolbar=1&navpanes=0`} title={name} />;
			if (!pdfBlob) return <Loading />;
			return (
				<Suspense fallback={<Loading />}>
					<PdfAttachmentPreview blob={pdfBlob} name={name} />
				</Suspense>
			);
		case 'text':
			return text === null ? (
				<Loading />
			) : (
				<Suspense fallback={<Loading />}>
					<TextFilePreview
						text={text}
						name={name}
						contentType={content.loaded.content_type}
						size={content.loaded.size}
						onDownload={content.download}
					/>
				</Suspense>
			);
		case 'csv':
			return text === null ? (
				<Loading />
			) : (
				<Suspense fallback={<Loading />}>
					<CsvFilePreview text={text} delimiter={csvDelimiterFor(name, content.loaded.content_type)} />
				</Suspense>
			);
		case 'html':
		case 'markdown':
			return text === null ? (
				<Loading />
			) : (
				<div className={styles.document}>
					<Suspense fallback={<Spinner />}>
						<DocumentAttachmentView kind={kind} text={text} name={name} />
					</Suspense>
				</div>
			);
		case 'audio':
			return (
				<div className={styles.empty}>
					{/* biome-ignore lint/a11y/useMediaCaption: user attachments carry no captions */}
					<audio controls src={blobUrl!} onError={content.fail} />
				</div>
			);
		case 'video':
			// biome-ignore lint/a11y/useMediaCaption: user attachments carry no captions
			return <video className={styles.video} controls src={blobUrl!} onError={content.fail} />;
		default:
			return (
				<div className={styles.empty}>
					<FileIcon size={40} />
					<span>{i18n._(isTooLargeToPreview(content.loaded) ? TOO_LARGE_DESCRIPTOR : NO_PREVIEW_DESCRIPTOR)}</span>
				</div>
			);
	}
}

/** The file on the preview's grey surface, filling the space it is given (in FilePreviewWindow). */
export function FileContentSurface({
	content,
	name,
	failedView,
	'data-flx': dataFlx,
}: {
	content: FileContent;
	name: string;
	failedView?: ReactNode;
	'data-flx'?: string;
}) {
	return (
		<div className={styles.surface} data-kind={content.kind} data-flx={dataFlx}>
			<FileContentBody content={content} name={name} failedView={failedView} />
		</div>
	);
}

/** Loads a file and shows it on the preview's grey surface, filling the space it is given. */
export function FileContentPreview({
	attachment,
	loadBlob,
	renderFailed,
	'data-flx': dataFlx,
}: {
	attachment: PreviewAttachment;
	loadBlob: () => Promise<Blob | null>;
	/** What to show when loading fails, given what the loader threw and a way to try again. */
	renderFailed?: (error: unknown, retry: () => void) => ReactNode;
	'data-flx'?: string;
}) {
	const content = useFileContent(attachment, loadBlob);
	// The caller's own card stands on its own, not on the grey surface.
	if (content.failed && renderFailed) return renderFailed(content.error, content.retry);
	return <FileContentSurface content={content} name={attachment.name} data-flx={dataFlx} />;
}
